import React, { useState, useEffect, useMemo } from 'react';
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiCopy,
  FiCheckSquare,
  FiFilter,
  FiLock
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../../Api';
import { usePermission } from '../../../hooks/usePermission';
import {
  PageComponent,
  Card,
  Table,
  Button,
  Input,
  Select,
  MultiSelect,
  Modal,
  Skeletons,
  TableActions,
} from '../../../components';
import InspectionModal from './InspectionModal';
import '../../CompanyManager/style.css';
import './style.css';

export default function InspectionConfig() {
  const permissions = usePermission('inspections');
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(['all']);

  const [inspections, setInspections] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [inspectionToDelete, setInspectionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [typeFilter, setTypeFilter] = useState('all');

  const INSPECTION_TYPES = {
    employee: 'Funcionário',
    free: 'Livre',
    station: 'Posto',
    vehicle: 'Veículo',
    infrastructure: 'Infraestrutura',
    others: 'Outros'
  };

  const typeOptions = [
    { value: 'all', label: 'Todos' },
    ...Object.entries(INSPECTION_TYPES).map(([value, label]) => ({ value, label }))
  ];

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = selectedCompanyIds;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map(c => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter(v => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter(v => v !== allValue);
      if (specificIds.length === companies.length && companies.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    setSelectedCompanyIds(nextValues);
  }

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!permissions.canView) return;
    (async () => {
      try {
        const data = await Api.getCompanies();
        const list = Array.isArray(data) ? data : (data.data || []);
        setCompanies(list);
        setSelectedCompanyIds(['all']);
      } catch (error) {
        toast.error('Erro ao carregar empresas.');
      }
    })();
  }, [permissions.canView]);

  useEffect(() => {
    loadInspections();
  }, [selectedCompanyIds, companies, permissions.canView, currentPage, pageSize, debouncedSearchTerm, typeFilter]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, typeFilter, selectedCompanyIds]);

  async function loadInspections() {
    if (!permissions.canView) return;
    if (!Array.isArray(selectedCompanyIds)) return;
    if (companies.length === 0) return;
 
    setLoading(true);
    try {
      const allValue = 'all';
      const hasAll = selectedCompanyIds.includes(allValue);
      const ids = hasAll
        ? companies.map(c => c.id)
        : selectedCompanyIds.filter(Boolean).map(id => Number(id));

      const uniqueIds = Array.from(new Set(ids));

      if (uniqueIds.length === 0) {
        setInspections([]);
        setTotalItems(0);
        setHasLoadedOnce(true);
      } else {
        const params = {
            page: currentPage,
            limit: pageSize,
            companyIds: uniqueIds.join(','),
        };

        if (debouncedSearchTerm) {
            params.searchTerm = debouncedSearchTerm;
        }

        if (typeFilter && typeFilter !== 'all') {
            params.type = typeFilter;
        }

        const response = await Api.listInspectionConfigs(params);
        
        if (response.data && Array.isArray(response.data)) {
            setInspections(response.data);
            setTotalItems(response.total || 0);
        } else {
            setInspections([]);
            setTotalItems(0);
        }

        setHasLoadedOnce(true);
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar inspeções.');
    } finally {
      setLoading(false);
    }
  }

  function handleCreate() {
    setSelectedInspection(null);
    setIsModalOpen(true);
  }

  function handleEdit(inspection) {
    setSelectedInspection(inspection);
    setIsModalOpen(true);
  }

  function handleClone(inspection) {
    // Remove id and timestamps to ensure a new creation
    // eslint-disable-next-line no-unused-vars
    const { id, createdAt, updatedAt, ...rest } = inspection;
    
    const clonedData = {
      ...rest,
      name: `${inspection.name} (Cópia)`,
    };
    setSelectedInspection(clonedData);
    setIsModalOpen(true);
  }

  function handleDelete(inspection) {
    setInspectionToDelete(inspection);
    setIsDeleteModalOpen(true);
  }

  async function confirmDelete() {
    if (!inspectionToDelete) return;
    const allValue = 'all';
    const hasAllSelected = selectedCompanyIds.includes(allValue);
    const explicitIds = selectedCompanyIds.filter(id => id && id !== allValue);
    const companyId = hasAllSelected || explicitIds.length === 0
      ? inspectionToDelete.company_id
      : explicitIds.length === 1
        ? Number(explicitIds[0])
        : inspectionToDelete.company_id;
    setIsDeleting(true);
    try {
      await Api.deleteInspectionConfig(companyId, inspectionToDelete.id);
      setInspections(prev => prev.filter(i => i.id !== inspectionToDelete.id));
      toast.success('Inspeção excluída com sucesso!');
      setIsDeleteModalOpen(false);
      setInspectionToDelete(null);
    } catch (error) {
      toast.error('Erro ao excluir inspeção.');
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleFormSuccess(data, companyIds) {
    if (!companyIds || companyIds.length === 0) {
      toast.error('Selecione uma empresa.');
      return;
    }

    // Resolve 'all' to actual IDs
     const allValue = 'all';
     let targetIds = [];
     if (companyIds.includes(allValue)) {
         targetIds = companies.map(c => String(c.id));
     } else {
         targetIds = companyIds.map(String);
     }
     targetIds = [...new Set(targetIds)]; // Unique

     try {
      // Clean company IDs (remove 'all' if present, ensure strings/numbers consistency)
      const validCompanyIds = targetIds
        .filter(id => id !== 'all')
        .map(id => Number(id))
        .filter(n => !isNaN(n));
      
      const payload = {
          ...data,
          company_ids: validCompanyIds
      };

      if (selectedInspection && selectedInspection.id) {
         // Update
         // Use the original company context if possible, or the first new one
         const originalCompanyId = selectedInspection.companies?.[0]?.id || selectedInspection.company_id || validCompanyIds[0];
         
         if (!originalCompanyId) {
             toast.error('Erro ao identificar empresa de origem.');
             return;
         }

         await Api.updateInspectionConfig(originalCompanyId, selectedInspection.id, payload);
         toast.success('Inspeção atualizada com sucesso!');
      } else {
         // Create
         if (validCompanyIds.length === 0) {
             toast.error('Selecione pelo menos uma empresa.');
             return;
         }
         
         // Use the first selected company as the context for creation
         const primaryCompanyId = validCompanyIds[0];
         await Api.createInspectionConfig(primaryCompanyId, payload);
         
         toast.success(`Inspeção criada e vinculada a ${validCompanyIds.length} empresa(s)!`);
      }
      
      loadInspections();
      setIsModalOpen(false);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao salvar inspeção.');
    }
  }

  const allValue = 'all';
  const hasAllSelected = selectedCompanyIds.includes(allValue);
  const explicitCompanyIds = selectedCompanyIds.filter(id => id && id !== allValue);
  const defaultCompanyId = !hasAllSelected && explicitCompanyIds.length === 1 ? explicitCompanyIds[0] : null;

  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 1;

  const columns = [
    {
      id: 'name',
      header: 'Nome da Inspeção',
      width: '2.2fr',
      cellClassName: 'manager-table-cell-primary',
      render: (item) => (
        <>
          <div className="manager-table-avatar" style={{ color: item.color || '#ccc' }}>
            <FiCheckSquare size={18} />
          </div>
          <div className="manager-table-cell-text">
            <span className="manager-table-title">{item.name}</span>
            <span className="manager-table-subtitle">ID: {item.id}</span>
          </div>
        </>
      )
    },
    {
      id: 'type',
      header: 'Tipo',
      width: '1.4fr',
      cellClassName: 'manager-table-cell',
      render: (item) => (
        <span className="manager-table-subtitle">{INSPECTION_TYPES[item.type] || item.type}</span>
      )
    },
    {
      id: 'sections',
      header: 'Execuções',
      width: '0.8fr',
      cellClassName: 'manager-table-cell',
      render: (item) => <span className="manager-table-subtitle">{item.executionCount > 0 ? `${item.executionCount} execução(s)` : 'Nenhuma'}</span>
    },
    {
      id: 'company',
      header: 'Empresa(s)',
      width: '1.4fr',
      cellClassName: 'manager-table-cell',
      render: (item) => {
        if (item.companies && item.companies.length > 0) {
            const names = item.companies.map(c => c.trade_name || c.corporate_name || c.name).join(', ');
            return (
                <span className="manager-table-subtitle" title={names}>
                    {item.companies.length > 1 ? `${item.companies.length} empresas` : names}
                </span>
            );
        }
        return <span className="manager-table-subtitle">-</span>;
      }
    },
    {
      id: 'actions',
      header: 'Ações',
      width: '0.6fr',
      cellClassName: 'manager-table-cell manager-table-col-actions',
      render: (item) => (
        <TableActions>
          {permissions.canEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(item);
              }}
            >
              <FiEdit2 size={16} />
              Editar
            </button>
          )}
          {permissions.canCreate && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClone(item);
              }}
            >
              <FiCopy size={16} />
              Clonar
            </button>
          )}
          {permissions.canDelete && (
            <button
              type="button"
              className="action-danger"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(item);
              }}
            >
              <FiTrash2 size={16} />
              Excluir
            </button>
          )}
        </TableActions>
      )
    }
  ];

  function InspectionConfigSkeletonWrapper() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  return (
    <PageComponent authenticatedPage={true}>
      {permissions.canView ? (
        <>
          <div className="container">
          {loading && !hasLoadedOnce && <InspectionConfigSkeletonWrapper />}

          {(hasLoadedOnce || !loading) && (
            <>
              <div className="manager-header">
               <div className="manager-header-title">
                 <div className="manager-header-icon">
                   <FiCheckSquare size={22} />
                 </div>
                 <div>
                   <h1>Configuração de Inspeções</h1>
                   <p>Crie e gerencie modelos de inspeção (checklists).</p>
                 </div>
               </div>
               <div className="manager-header-actions">
                 {permissions.canCreate && (
                   <Button onClick={handleCreate} className="manager-header-button">
                     <FiPlus size={18} />
                     Nova Inspeção
                   </Button>
                 )}
               </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
                {inspections.length > 0 && (
                  <span className="manager-filters-count">
                    {totalItems} inspeções encontradas
                  </span>
                )}
              </div>

              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Empresa</label>
                  <MultiSelect
                    id="filter-company"
                    options={[
                      { value: 'all', label: 'TODAS' },
                      ...companies.map(c => ({
                        value: String(c.id),
                        label: c.corporate_name || c.trade_name || c.name || `Empresa ${c.id}`
                      }))
                    ]}
                    value={selectedCompanyIds}
                    onChange={handleCompanyFilterChange}
                    searchable
                    placeholder="Selecione empresas..."
                  />
                </div>
                <div className="manager-filter-group">
                  <label className="manager-filter-label">Buscar</label>
                  <div className="manager-filter-search">
                     <Input 
                       id="inspection-search"
                       placeholder="Buscar por nome..." 
                       value={searchTerm}
                       onChange={e => setSearchTerm(e.target.value)}
                       icon={FiSearch}
                     />
                  </div>
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Tipo</label>
                  <Select
                    id="filter-type"
                    options={typeOptions}
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value)}
                  />
                </div>
              </div>
            </Card>

            {inspections.length === 0 ? (
              <div className="empty-state">
                <p>Nenhuma inspeção cadastrada.</p>
              </div>
            ) : (
              <Table
                items={inspections}
                columns={columns}
                totalItems={totalItems}
                page={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={value => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                getRowKey={item => item.id}
                emptyMessage="Nenhuma inspeção encontrada com os filtros atuais."
              />
            )}
          </>
        )}
      </div>

      <InspectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialData={selectedInspection}
        onSuccess={handleFormSuccess}
        defaultCompanyId={defaultCompanyId}
        companies={companies}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar exclusão"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir a inspeção{' '}
            {inspectionToDelete && (
              <span className="delete-modal-company-name">
                {inspectionToDelete.name}
              </span>
            )}
            ?
          </p>
          <p>
            Essa ação não poderá ser desfeita.
          </p>
        </div>
        <div className="delete-modal-actions">
          <Button type="button" className="cancel-button" onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>Cancelar</Button>
          <Button type="button" onClick={confirmDelete} disabled={isDeleting}>
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>
        </>
      ) : (
        <div className="container">
          <div className="manager-header">
            <div className="manager-header-title">
              <div className="manager-header-icon">
                <FiLock size={22} />
              </div>
              <div>
                <h1>Acesso Negado</h1>
                <p>Você não tem permissão para visualizar este módulo.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageComponent>
  );
}
